import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Trophy, Sparkles, Clock, Target, LayoutGrid, List } from 'lucide-react';
import { useSteamUser } from '../hooks/useSteamUser';
import { UserProfileHeader } from '../components/UserProfileHeader';
import { BADGES, LEVELS, calculateUserLevel, calculateProgress, calculateBadges, calculateTotalExperience, getBadgesByCategory } from '../utils/badges';
import { BadgeGrid } from '../components/BadgeGrid';
import { BadgeList } from '../components/BadgeList';

type CategoryFilter = 'all' | 'playtime' | 'achievements';
type ViewMode = 'grid' | 'list';

export function UserBadgesPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, gameStats, isLoading, error } = useSteamUser(username || '');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user || !gameStats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Failed to load user data'}</p>
          <button
            onClick={() => navigate(`/user/${username}`)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to Profile
          </button>
        </div>
      </div>
    );
  }

  const totalPlaytimeHours = Math.floor(gameStats.totalPlaytime / 60);
  const totalAchievements = gameStats.achievementStats.unlocked;
  const earnedBadges = calculateBadges(totalPlaytimeHours, totalAchievements);
  const experience = calculateTotalExperience(earnedBadges);
  const currentLevel = calculateUserLevel(experience);
  const progress = calculateProgress(experience);
  const nextLevel = LEVELS[currentLevel.level] || LEVELS[LEVELS.length - 1];

  const filteredBadges = BADGES.filter(badge => {
    if (categoryFilter === 'all') return true;
    return badge.category === categoryFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(`/user/${username}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Profile
        </button>

        <UserProfileHeader user={user} gameStats={gameStats} />

        <div className="mt-6 space-y-6">
          {/* Level Progress Card */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg p-8 text-white">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <Star className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Level {currentLevel.level}</h2>
                  <p className="text-indigo-100">{currentLevel.title}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-indigo-100">Total Experience</p>
                <p className="text-2xl font-bold">{experience.toLocaleString()} XP</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress to Level {currentLevel.level + 1}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-4 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-indigo-100">
                {experience.toLocaleString()} / {nextLevel.experienceRequired.toLocaleString()} XP
              </p>
            </div>
          </div>

          {/* Filters and View Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex gap-4">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Trophy className="w-5 h-5" />
                <span>All Badges</span>
              </button>
              <button
                onClick={() => setCategoryFilter('playtime')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  categoryFilter === 'playtime'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Clock className="w-5 h-5" />
                <span>Playtime</span>
              </button>
              <button
                onClick={() => setCategoryFilter('achievements')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  categoryFilter === 'achievements'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Target className="w-5 h-5" />
                <span>Achievements</span>
              </button>
            </div>

            <div className="flex gap-2 bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-indigo-100 text-indigo-600'
                    : 'text-gray-600 hover:text-indigo-600'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="text-sm font-medium">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-indigo-100 text-indigo-600'
                    : 'text-gray-600 hover:text-indigo-600'
                }`}
              >
                <List className="w-4 h-4" />
                <span className="text-sm font-medium">List</span>
              </button>
            </div>
          </div>

          {/* Badges Display */}
          {viewMode === 'grid' ? (
            <BadgeGrid
              badges={filteredBadges}
              earnedBadges={earnedBadges}
              totalPlaytimeHours={totalPlaytimeHours}
              totalAchievements={totalAchievements}
            />
          ) : (
            <BadgeList
              badges={filteredBadges}
              earnedBadges={earnedBadges}
              totalPlaytimeHours={totalPlaytimeHours}
              totalAchievements={totalAchievements}
            />
          )}
        </div>
      </div>
    </div>
  );
}